import { api } from './client'

/**
 * F-080 (TASK-0334): regole configurabili dei lead, solo Admin (`LeadRulesController`, `api/lead-rules`).
 * Tre famiglie: punteggio (REQ-087-001), proprietà sul cliente riacquisito (REQ-107-001), escalation per
 * inattività (REQ-074-001). Nessuna regola è seminata.
 */

/** Campi del lead su cui una regola di punteggio può fare condizione. */
export const SCORING_FIELDS = [
  { value: 'lead_type', label: 'Tipo di lead (person / company)' },
  { value: 'interest_sector', label: 'Settore di interesse' },
  { value: 'source', label: 'Fonte (codice)' },
  { value: 'temperature', label: 'Temperatura (cold / warm / hot / very_hot)' },
  { value: 'campaign_id', label: 'Campagna (id)' },
  { value: 'call_attempts', label: 'Tentativi di chiamata' },
  { value: 'has_email', label: "Ha l'email (true / false)" },
  { value: 'has_phone', label: 'Ha il telefono (true / false)' },
  { value: 'last_outcome', label: "Codice dell'ultimo esito di chiamata" },
] as const

export const SCORING_OPERATORS = [
  { value: 'eq', label: 'è uguale a' },
  { value: 'ne', label: 'è diverso da' },
  { value: 'gt', label: 'è maggiore di' },
  { value: 'lt', label: 'è minore di' },
  { value: 'contains', label: 'contiene' },
] as const

export interface ScoringCondition {
  field: string
  operator: string
  value: string
}

export interface ScoringRule {
  id: number
  name: string
  /** JSON array di `{field, operator, value}`, al massimo 5 condizioni. */
  conditionsJson: string
  /** Da -100 a 100. */
  points: number
  isActive: boolean
  sortOrder: number
  createdAt: string
  updatedAt?: string | null
}

export interface ScoringRuleBody {
  name: string
  conditionsJson: string
  points: number
  isActive: boolean
  sortOrder: number
}

export type OwnershipAction = 'MantieniProprietario' | 'AssegnaNuovo' | 'TeamLeader'

export interface OwnershipRule {
  id: number
  name: string
  priority: number
  interestSector?: string | null
  source?: string | null
  minDaysSinceOwnerActivity?: number | null
  maxDaysSinceOwnerActivity?: number | null
  action: OwnershipAction
  isActive: boolean
  createdAt: string
  updatedAt?: string | null
}

export interface OwnershipRuleBody {
  name: string
  action: OwnershipAction
  priority: number
  interestSector?: string | null
  source?: string | null
  minDaysSinceOwnerActivity?: number | null
  maxDaysSinceOwnerActivity?: number | null
  isActive: boolean
}

export interface EscalationPolicy {
  id: number
  name: string
  source?: string | null
  campaignId?: number | null
  /** Minuti senza attività dall'assegnazione prima di avvisare l'agente. */
  notifyAgentAfterMinutes: number
  /** Minuti ulteriori, dopo l'avviso all'agente, prima di avvisare il responsabile. */
  notifyTeamLeaderAfterMinutes: number
  isActive: boolean
  createdAt: string
  updatedAt?: string | null
}

export interface EscalationPolicyBody {
  name: string
  notifyAgentAfterMinutes: number
  notifyTeamLeaderAfterMinutes: number
  source?: string | null
  campaignId?: number | null
  isActive: boolean
}

function crud<TItem, TBody>(kind: 'scoring' | 'ownership' | 'escalation') {
  const base = `/api/lead-rules/${kind}`
  return {
    list: () => api<TItem[]>(base),
    create: (body: TBody) => api<{ id: number }>(base, { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: TBody) => api<void>(`${base}/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    remove: (id: number) => api<void>(`${base}/${id}`, { method: 'DELETE' }),
  }
}

export const leadRulesApi = {
  scoring: {
    ...crud<ScoringRule, ScoringRuleBody>('scoring'),
    /** Ricalcola il punteggio di tutti i lead in background; risposta 202 con l'id del job (seguibile in /jobs). */
    recalculate: () => api<{ jobId: number }>('/api/lead-rules/scoring/recalculate', { method: 'POST' }),
  },
  ownership: crud<OwnershipRule, OwnershipRuleBody>('ownership'),
  escalation: crud<EscalationPolicy, EscalationPolicyBody>('escalation'),
}
